import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAnalystBench, NavSection, RatioCategory, getInstitutionDataAsync } from '../../context/AnalystBenchContext';
import { useRoleAssumption } from '../../context/RoleAssumptionContext';
import { getReportsForInstitution, type ReportWorkflow } from '../../services/firestore';
import './NavigationPanel.css';

// SVG Icons
const ProfileIcon = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M19 21v-2a4 4 0 0 0-4-4H9a4 4 0 0 0-4 4v2"/>
    <circle cx="12" cy="7" r="4"/>
  </svg>
);

const FinancialsIcon = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <rect x="3" y="3" width="18" height="18" rx="2"/>
    <path d="M3 9h18"/>
    <path d="M9 21V9"/>
  </svg>
);

const RatiosIcon = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <line x1="12" y1="20" x2="12" y2="10"/>
    <line x1="18" y1="20" x2="18" y2="4"/>
    <line x1="6" y1="20" x2="6" y2="16"/>
  </svg>
);

const QuantIcon = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <rect x="3" y="3" width="7" height="7"/>
    <rect x="14" y="3" width="7" height="7"/>
    <rect x="14" y="14" width="7" height="7"/>
    <rect x="3" y="14" width="7" height="7"/>
  </svg>
);

const QualIcon = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M9 11l3 3L22 4"/>
    <path d="M21 12v7a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11"/>
  </svg>
);

const CreditIcon = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="12" cy="12" r="10"/>
    <path d="M12 6v6l4 2"/>
  </svg>
);

const ReportsIcon = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/>
    <path d="M14 2v6h6"/>
    <line x1="16" y1="13" x2="8" y2="13"/>
    <line x1="16" y1="17" x2="8" y2="17"/>
    <line x1="10" y1="9" x2="8" y2="9"/>
  </svg>
);

const OverviewIcon = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z"/>
    <polyline points="9 22 9 12 15 12 15 22"/>
  </svg>
);

const FolderIcon = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M22 19a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h5l2 3h9a2 2 0 0 1 2 2z"/>
  </svg>
);

const FileIcon = () => (
  <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/>
    <path d="M14 2v6h6"/>
  </svg>
);

// Status display config — labelKey is resolved via t() at render time
const STATUS_CONFIG: Record<string, { labelKey: string; order: number }> = {
  draft: { labelKey: 'status.drafts', order: 1 },
  pending_admin: { labelKey: 'status.submitted', order: 2 },
  client_review: { labelKey: 'status.clientReview', order: 3 },
  pending_committee: { labelKey: 'status.committee', order: 4 },
  approved: { labelKey: 'status.approved', order: 5 },
  rejected: { labelKey: 'status.rejected', order: 6 },
};

interface NavItemConfig {
  id: NavSection;
  labelKey: string;
  icon: React.FC;
  children?: { id: RatioCategory; labelKey: string }[];
}

const NAV_ITEMS: NavItemConfig[] = [
  { id: 'landing', labelKey: 'nav.overview', icon: OverviewIcon },
  { id: 'company-profile', labelKey: 'nav.profile', icon: ProfileIcon },
  { id: 'financials', labelKey: 'nav.financials', icon: FinancialsIcon },
  { id: 'key-ratios', labelKey: 'nav.keyRatios', icon: RatiosIcon },
  { id: 'quantitative-rating', labelKey: 'nav.quantitativeRating', icon: QuantIcon },
  { id: 'qualitative-rating', labelKey: 'nav.qualitativeRating', icon: QualIcon },
  { id: 'credit-rating', labelKey: 'nav.creditRating', icon: CreditIcon },
  { id: 'reports', labelKey: 'nav.reports', icon: ReportsIcon },
];

export default function NavigationPanel() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const {
    selectedInstitution,
    activeSection,
    setActiveSection,
    activeRatioCategory,
    setActiveRatioCategory,
    defaultPeriod,
    setReportsPeriod,
  } = useAnalystBench();
  const { effectiveOrganization } = useRoleAssumption();

  const [expandedItems, setExpandedItems] = useState<Set<NavSection>>(new Set());
  const [expandedFolders, setExpandedFolders] = useState<Set<string>>(new Set());
  const [institutionReports, setInstitutionReports] = useState<ReportWorkflow[]>([]);
  const [loadingReports, setLoadingReports] = useState(false);

  // Rating data for the institution (fetched async from API)
  const [periodData, setPeriodData] = useState<{ rating: string } | null>(null);

  // Fetch rating data from API
  useEffect(() => {
    const fetchRating = async () => {
      if (!selectedInstitution?.id) {
        setPeriodData(null);
        return;
      }
      const data = await getInstitutionDataAsync(selectedInstitution.id);
      setPeriodData(data ? { rating: data.rating } : null);
    };
    fetchRating();
  }, [selectedInstitution?.id]);

  // Format period for display (e.g., "2025-05" -> "May 2025")
  const formatPeriodDisplay = (period: string): string => {
    const [year, month] = period.split('-');
    const date = new Date(parseInt(year), parseInt(month) - 1);
    return date.toLocaleDateString('en-US', { month: 'short', year: 'numeric' });
  };

  // Fetch reports for the selected institution
  useEffect(() => {
    const fetchReports = async () => {
      if (!selectedInstitution?.id || !effectiveOrganization?.id) {
        setInstitutionReports([]);
        return;
      }

      setLoadingReports(true);
      try {
        const reports = await getReportsForInstitution(
          selectedInstitution.id,
          effectiveOrganization.id
        );
        setInstitutionReports(reports);
      } catch (error) {
        console.error('Error loading reports:', error);
        setInstitutionReports([]);
      } finally {
        setLoadingReports(false);
      }
    };

    fetchReports();
  }, [selectedInstitution?.id, effectiveOrganization?.id]);

  // Group reports by status
  const reportsByStatus = institutionReports.reduce((acc, report) => {
    const status = report.status;
    if (!acc[status]) {
      acc[status] = [];
    }
    acc[status].push(report);
    return acc;
  }, {} as Record<string, ReportWorkflow[]>);

  // Sort folders by status order
  const sortedStatuses = Object.keys(reportsByStatus).sort((a, b) => {
    return (STATUS_CONFIG[a]?.order || 99) - (STATUS_CONFIG[b]?.order || 99);
  });

  const toggleFolder = (status: string) => {
    setExpandedFolders(prev => {
      const next = new Set(prev);
      if (next.has(status)) {
        next.delete(status);
      } else {
        next.add(status);
      }
      return next;
    });
  };

  const formatPeriod = (period: string): string => {
    const [year, month] = period.split('-');
    const date = new Date(parseInt(year), parseInt(month) - 1);
    return date.toLocaleDateString('en-US', { month: 'short', year: 'numeric' });
  };

  const handleReportClick = (report: ReportWorkflow) => {
    // Navigate to the Reports view with the report's period selected
    // User can then click "Continue Editing" or "View Report" from there
    setActiveSection('reports');
    setReportsPeriod(report.period);
  };

  const toggleExpand = (section: NavSection) => {
    setExpandedItems(prev => {
      const next = new Set(prev);
      if (next.has(section)) {
        next.delete(section);
      } else {
        next.add(section);
      }
      return next;
    });
  };

  const handleItemClick = (item: NavItemConfig) => {
    // Toggle expand for items with children OR for the reports section (which has its own folder structure)
    if (item.children || item.id === 'reports') {
      toggleExpand(item.id);
    }
    setActiveSection(item.id);
    setActiveRatioCategory(null);
  };

  const handleChildClick = (parentId: NavSection, childId: RatioCategory) => {
    setActiveSection(parentId);
    setActiveRatioCategory(childId);
  };

  return (
    <nav className="nav-panel">
      {/* Institution Info Card */}
      {selectedInstitution && (
        <div className="nav-institution-card">
          <div className="institution-info">
            <h4 className="institution-name">{selectedInstitution.name}</h4>
            {defaultPeriod && (
              <span className="institution-period">Last update: {formatPeriodDisplay(defaultPeriod)}</span>
            )}
          </div>
        </div>
      )}

      <div className="nav-items">
        {NAV_ITEMS.map(item => {
          const IconComponent = item.icon;
          const isReportsSection = item.id === 'reports';
          const hasReports = institutionReports.length > 0;

          return (
            <div key={item.id} className="nav-item-group">
              {/* Add separator before Reports section */}
              {isReportsSection && <div className="nav-separator" />}

              <button
                className={`nav-item ${activeSection === item.id && !activeRatioCategory ? 'active' : ''} ${item.children || isReportsSection ? 'has-children' : ''}`}
                onClick={() => handleItemClick(item)}
              >
                <span className="nav-icon">
                  <IconComponent />
                </span>
                <span className="nav-label">{t(item.labelKey)}</span>
                {isReportsSection && hasReports && (
                  <span className="nav-badge">{institutionReports.length}</span>
                )}
                {(item.children || (isReportsSection && hasReports)) && (
                  <svg
                    className={`nav-expand-icon ${expandedItems.has(item.id) ? 'expanded' : ''}`}
                    width="14"
                    height="14"
                    viewBox="0 0 20 20"
                    fill="none"
                  >
                    <path d="M7.5 5L12.5 10L7.5 15" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                )}
              </button>

              {/* Regular children (for non-reports sections) */}
              {item.children && expandedItems.has(item.id) && (
                <div className="nav-children">
                  {item.children.map(child => (
                    <button
                      key={child.id}
                      className={`nav-child ${activeSection === item.id && activeRatioCategory === child.id ? 'active' : ''}`}
                      onClick={() => handleChildClick(item.id, child.id)}
                    >
                      <span className="child-indicator" />
                      <span className="child-label">{t(child.labelKey)}</span>
                    </button>
                  ))}
                </div>
              )}

              {/* Reports folder structure */}
              {isReportsSection && expandedItems.has(item.id) && (
                <div className="nav-reports-tree">
                  {loadingReports ? (
                    <div className="nav-reports-loading">{t('common.loading')}</div>
                  ) : sortedStatuses.length === 0 ? (
                    <div className="nav-reports-empty">{t('reports.empty')}</div>
                  ) : (
                    sortedStatuses.map(status => {
                      const reports = reportsByStatus[status] || [];
                      const isExpanded = expandedFolders.has(status);
                      const statusConfig = STATUS_CONFIG[status];

                      return (
                        <div key={status} className="nav-folder-group">
                          <button
                            className={`nav-folder ${isExpanded ? 'expanded' : ''}`}
                            onClick={() => toggleFolder(status)}
                          >
                            <svg
                              className={`nav-folder-expand ${isExpanded ? 'expanded' : ''}`}
                              width="10"
                              height="10"
                              viewBox="0 0 20 20"
                              fill="none"
                            >
                              <path d="M7.5 5L12.5 10L7.5 15" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                            </svg>
                            <span className="nav-folder-icon">
                              <FolderIcon />
                            </span>
                            <span className="nav-folder-label">{statusConfig ? t(statusConfig.labelKey) : status}</span>
                            <span className="nav-folder-count">{reports.length}</span>
                          </button>

                          {isExpanded && (
                            <div className="nav-folder-files">
                              {reports.map(report => (
                                <button
                                  key={report.id}
                                  className="nav-file"
                                  onClick={() => handleReportClick(report)}
                                  title={`${formatPeriod(report.period)} - v${report.version}`}
                                >
                                  <span className="nav-file-icon">
                                    <FileIcon />
                                  </span>
                                  <span className="nav-file-label">{formatPeriod(report.period)}</span>
                                </button>
                              ))}
                            </div>
                          )}
                        </div>
                      );
                    })
                  )}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </nav>
  );
}
